import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { matchesAPI } from '../services/api';
import NotificationBell from './NotificationBell';
import './Navigation.css';
import logo from '../assets/images/logo-light.svg';

const Navigation: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);
  const siteUrl = process.env.REACT_APP_SITE_URL || '';
  const avatarSrc = user?.avatar_url ? `${siteUrl}${user.avatar_url}` : null;
  const initials = user ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() : '';
  const navItems = [
    { to: '/', label: 'Acasă', public: true, end: true },
    { to: '/dashboard', label: 'Panou' },
    { to: '/clubs', label: 'Cluburi' },
    { to: '/tournaments', label: 'Turnee' },
    { to: '/players', label: 'Jucători' },
    { to: '/matches', label: 'Meciuri' },
    { to: '/rankings', label: 'Clasament' },
  ];

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const loadPendingActions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPendingActionsCount(0);
      return;
    }

    try {
      const [matchesRes, openRes] = await Promise.all([
        matchesAPI.getAll({ mine: true }),
        matchesAPI.getOpenMatches({ status: 'open' }),
      ]);

      const allMatches = (matchesRes.success && matchesRes.data?.matches) ? matchesRes.data.matches : [];
      const openMatches = (openRes.success && openRes.data?.open_matches) ? openRes.data.open_matches : [];

      const confirmationsToHandle = allMatches.filter(m =>
        m.status === 'pending_confirmation' &&
        m.confirmation_status === 'pending' &&
        m.submitted_by !== user.id,
      ).length;

      const openForMyLevel = openMatches.filter(ch => {
        if (ch.created_by === user.id) return false;
        const allowedLevels = ch.allowed_levels || [];
        if (allowedLevels.length === 0) return true;
        if (!user.level_id) return false;
        return allowedLevels.some(level => level.id === user.level_id);
      }).length;

      setPendingActionsCount(confirmationsToHandle + openForMyLevel);
    } catch {
      // Keep nav usable even when pending-actions fetch fails.
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPendingActions();
  }, [loadPendingActions, location.pathname]);

  useEffect(() => {
    const handleRefresh = () => {
      loadPendingActions();
    };

    window.addEventListener('matches:pending-actions-changed', handleRefresh);
    return () => {
      window.removeEventListener('matches:pending-actions-changed', handleRefresh);
    };
  }, [loadPendingActions]);

  const visibleNavItems = navItems.filter(item => item.public || isAuthenticated);

  const navLabel = (item: { to: string; label: string }) => {
    if (item.to !== '/matches' || pendingActionsCount <= 0) return item.label;
    return (
      <span className="nav-link-with-badge">
        <span>{item.label}</span>
        <span className="nav-item-count">{pendingActionsCount}</span>
      </span>
    );
  };

  const userMenu = (
    <li className="nav-item dropdown">
      <button
        className="nav-link nav-user-btn dropdown-toggle"
        type="button"
        id="userMenu"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <span className="nav-avatar" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: avatarSrc ? 'transparent' : 'var(--tt-primary)',
          color: '#fff', fontWeight: 700, fontSize: '.8rem', overflow: 'hidden',
        }}>
          {avatarSrc
            ? <img src={avatarSrc} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : initials
          }
        </span>
        {user?.first_name}
      </button>
      <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userMenu">
        <li><Link className="dropdown-item" to="/profile">Profilul Meu</Link></li>
        <li><Link className="dropdown-item" to="/bookings">Rezervările Mele</Link></li>
        <li><hr className="dropdown-divider" /></li>
        <li>
          <button
            className="dropdown-item text-danger"
            onClick={() => logout()}
            style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
          >
            Deconectare
          </button>
        </li>
      </ul>
    </li>
  );

  return (
    <nav className="tt-nav">
      <div className="container tt-nav-inner">
        <Link className="navbar-brand" to="/">
          <img src={logo} alt="TT Platform" />
        </Link>

        <div className="nav-mobile-actions">
          {isAuthenticated && <NotificationBell />}
          <button
            className={`nav-menu-toggle ${mobileOpen ? 'is-open' : ''}`}
            type="button"
            onClick={() => setMobileOpen(prev => !prev)}
            aria-label="Meniu"
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div className="tt-nav-desktop">
          <ul className="navbar-nav me-auto gap-1">
            {visibleNavItems.map(item => (
              <li className="nav-item" key={item.to}>
                <NavLink className="nav-link" to={item.to} end={item.end}>{navLabel(item)}</NavLink>
              </li>
            ))}
          </ul>

          <ul className="navbar-nav align-items-lg-center gap-2">
            {isAuthenticated ? (
              <>
                <NotificationBell />
                {userMenu}
              </>
            ) : (
              <>
                <li className="nav-item">
                  <Link className="nav-link nav-ghost" to="/login">Autentificare</Link>
                </li>
                <li className="nav-item">
                  <Link className="btn nav-cta" to="/register">Înregistrare</Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      <div className={`nav-drawer-backdrop ${mobileOpen ? 'is-open' : ''}`} onClick={() => setMobileOpen(false)} />
      <aside className={`nav-mobile-drawer ${mobileOpen ? 'is-open' : ''}`} aria-hidden={!mobileOpen}>
        <div className="nav-drawer-head">
          <img src={logo} alt="TT Platform" />
          <button className="nav-drawer-close" type="button" onClick={() => setMobileOpen(false)} aria-label="Închide meniul">×</button>
        </div>

        {isAuthenticated && user && (
          <Link className="nav-drawer-user" to="/profile">
            <span className="nav-avatar nav-avatar-lg" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: avatarSrc ? 'transparent' : 'var(--tt-primary)',
              color: '#fff', fontWeight: 800, overflow: 'hidden',
            }}>
              {avatarSrc
                ? <img src={avatarSrc} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : initials
              }
            </span>
            <span>
              <strong>{user.first_name} {user.last_name}</strong>
              <small>{user.level_name || user.email}</small>
            </span>
          </Link>
        )}

        <div className="nav-drawer-links">
          {visibleNavItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} className="nav-drawer-link">
              {navLabel(item)}
            </NavLink>
          ))}
        </div>

        <div className="nav-drawer-footer">
          {isAuthenticated ? (
            <>
              <Link className="nav-drawer-secondary" to="/bookings">Rezervările mele</Link>
              <button className="nav-drawer-danger" type="button" onClick={() => { setMobileOpen(false); logout(); }}>
                Deconectare
              </button>
            </>
          ) : (
            <>
              <Link className="nav-drawer-secondary" to="/login">Autentificare</Link>
              <Link className="nav-drawer-primary" to="/register">Înregistrare</Link>
            </>
          )}
        </div>
      </aside>
    </nav>
  );
};

export default Navigation;
